/**
 * Error Boundary for Analysis charts.
 *
 * Catches rendering errors in chart components and displays
 * a friendly error message with retry option.
 */
import { Component, ReactNode } from 'react';
import { Paper, Stack, Text, ThemeIcon, Button } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

interface Props {
  children: ReactNode;
  /** Optional name for the chart section (for error display) */
  chartName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class AnalysisErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error for debugging
    console.error('Analysis chart error:', error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { chartName } = this.props;
      const displayName = chartName ? `${chartName} chart` : 'chart';

      return (
        <Paper p="xl" withBorder radius="md" style={{ minHeight: 200 }}>
          <Stack align="center" justify="center" gap="md" h="100%" mih={150}>
            <ThemeIcon size={48} radius="md" variant="light" color="red">
              <IconAlertTriangle size={28} />
            </ThemeIcon>
            <Stack align="center" gap="xs">
              <Text size="lg" fw={600}>Failed to render {displayName}</Text>
              <Text size="sm" c="dimmed" ta="center" maw={400}>
                An error occurred while displaying the chart data.
                This may be due to unexpected data format.
              </Text>
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Text size="xs" c="red" ta="center" maw={500} style={{ fontFamily: 'monospace' }}>
                  {this.state.error.message}
                </Text>
              )}
            </Stack>
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="light"
              onClick={this.handleRetry}
            >
              Try Again
            </Button>
          </Stack>
        </Paper>
      );
    }

    return this.props.children;
  }
}
